import React, { useContext, useMemo } from "react";
import { ControlContext } from "../ControlContext.jsx";
import * as THREE from "three";
import { withCrossOrigin } from "../utils/ensureTextureCrossOrigin";
// import { NURBSSurface, NURBSUtils } from 'three/examples/jsm/curves/NURBSUtils';
// import { getSurfacePoint } from 'three/examples/jsm/curves/NURBSUtils';

import FloatProfile from "./FrameComp/FloatFrame/FloatProfile.jsx";

function Plane({
  width,
  height,
  position,
  rotation,
  txtrScale,
  txtrOffset,
  canvasBorder,
  imageFile,
  canvasMaterial,
}) {
  const materialRender = useMemo(() => {
    if (canvasBorder === "Gallery Wrap") {
      const material = new THREE.MeshStandardMaterial({
        color: "#000000",
        emissive: "#ffffff",
        emissiveIntensity: 1,
      });

      material.emissiveMap = withCrossOrigin(new THREE.TextureLoader()).load(
        imageFile
      );
      // material.emissiveMap.colorSpace = THREE.SRGBColorSpace;

      material.roughnessMap = canvasMaterial.roughnessMap.clone();
      material.normalMap = canvasMaterial.normalMap.clone();

      if (material.emissiveMap) {
        material.emissiveMap.flipY = false;
        material.emissiveMap.repeat.set(txtrScale[0], txtrScale[1]);
        material.emissiveMap.offset.set(txtrOffset[0], txtrOffset[1]);
      }
      material.roughnessMap.flipY = false;
      material.roughnessMap.repeat.set(txtrScale[0], txtrScale[1]);
      material.roughnessMap.offset.set(txtrOffset[0], txtrOffset[1]);

      material.normalMap.flipY = false;
      material.normalMap.repeat.set(txtrScale[0], txtrScale[1]);
      material.normalMap.offset.set(txtrOffset[0], txtrOffset[1]);
      return material;
    } else {
      const material = new THREE.MeshStandardMaterial({
        color: canvasBorder,
        roughnessMap: canvasMaterial.roughnessMap,
        normalMap: canvasMaterial.normalMap,
      });

      material.roughnessMap = canvasMaterial.roughnessMap.clone();
      material.normalMap = canvasMaterial.normalMap.clone();

      material.roughnessMap.flipY = false;
      material.roughnessMap.repeat.set(txtrScale[0], txtrScale[1]);
      material.roughnessMap.offset.set(txtrOffset[0], txtrOffset[1]);

      material.normalMap.flipY = false;
      material.normalMap.repeat.set(txtrScale[0], txtrScale[1]);
      material.normalMap.offset.set(txtrOffset[0], txtrOffset[1]);
      return material;
    }
  }, [canvasBorder, imageFile]);

  return (
    <mesh
      material={materialRender}
      receiveShadow
      castShadow
      position={position}
      rotation={rotation}
    >
      <planeGeometry args={[width, height]} />
    </mesh>
  );
}

export default function CanvasFrame() {
  const { printWidth, printHeight, imageFile, canvasBorder, canvasFrame } =
    useContext(ControlContext);

  const canvasDepth = canvasFrame ? 1.3 : 0;

  const roughnessMap = useMemo(
    () =>
      withCrossOrigin(new THREE.TextureLoader()).load(
        "./textures/paper/canvas/Canvas_4k_roughness.jpeg"
      ),
    []
  );
  const normalMap = useMemo(
    () =>
      withCrossOrigin(new THREE.TextureLoader()).load(
        "./textures/paper/canvas/Canvas_4k_normal.jpeg"
      ),
    []
  );

  const canvasMaterial = useMemo(() => {
    const material = new THREE.MeshStandardMaterial({
      color: "#000000",
      emissive: "#ffffff",
      emissiveIntensity: 2,
      roughnessMap: roughnessMap,
      normalMap: normalMap,
    });

    material.emissiveMap = withCrossOrigin(new THREE.TextureLoader()).load(
      imageFile
    );
    // material.emissiveMap.colorSpace = THREE.SRGBColorSpace;

    return material;
  }, [imageFile, roughnessMap, normalMap]);

  const renderCanvasFrame = useMemo(() => {
    if (canvasFrame) {
      return (
        <FloatProfile
          pWidthOverride={1.2}
          pDepthOverride={4.5}
          pInnerDepthOverride={3.5}
          pInnerWidthOverride={5}
          widthOverride={printWidth + 0.4}
          heightOverride={printHeight + 0.4}
        />
      );
    }
  }, [canvasFrame, printWidth]);

  const props = {
    canvasBorder,
    imageFile,
    canvasMaterial,
  };

  return (
    <>
      <group position={[0, 0, canvasDepth]}>
        <Plane
          width={printWidth}
          height={3}
          position={[0, printHeight / 2, 1.5]}
          rotation={[-Math.PI / 2, 0, 0]}
          txtrScale={[1, 0.05]}
          txtrOffset={[0, 0]}
          {...props}
        />
        <Plane
          width={printWidth}
          height={3}
          position={[0, -printHeight / 2, 1.5]}
          rotation={[Math.PI / 2, 0, 0]}
          txtrScale={[1, 0.05]}
          txtrOffset={[0, 0.95]}
          {...props}
        />
        <Plane
          width={3}
          height={printHeight}
          position={[printWidth / 2, 0, 1.5]}
          rotation={[0, Math.PI / 2, Math.PI]}
          txtrScale={[0.05, 1]}
          txtrOffset={[0.95, 0]}
          {...props}
        />
        <Plane
          width={3}
          height={printHeight}
          position={[-printWidth / 2, 0, 1.5]}
          rotation={[0, -Math.PI / 2, Math.PI]}
          txtrScale={[0.05, 1]}
          txtrOffset={[0, 0]}
          {...props}
        />
        <mesh material={canvasMaterial} position={[0, 0, 3]}>
          <planeGeometry args={[printWidth, printHeight]} />
        </mesh>
      </group>
      {renderCanvasFrame}
    </>
  );
}
